import { useEffect, useState } from "react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AiChatWidget from "@/components/site/AiChatWidget";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowUpRight, HardHat, Cpu, GraduationCap, Users, Building2,
  Fuel, Zap, HeartPulse, Car, MapPin, Mail, Phone, FileText, Send,
  Shield, Rocket, Handshake, Sparkles
} from "lucide-react";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758704961735-ded8bb22b378?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwb2lsJTIwcmlnJTIwbmlnaHR8ZW58MHx8fHwxNzg2MTkxMDUxfDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: HardHat, title: "Construction & Industrial / EPC", desc: "Engineering, procurement and construction of industrial plants, sub-stations and large warehouses. Onshore & offshore project shutdown and maintenance." },
  { icon: Cpu, title: "Information Technology", desc: "AI-ready IT services — data-center enablement, industrial software integration, and digital transformation for heavy industry." },
  { icon: GraduationCap, title: "Skill Training & Development", desc: "Industry-aligned academies training the next generation of GCC-ready technicians — in partnership with the Government of Telangana." },
  { icon: Users, title: "Recruitment Services", desc: "Global sourcing of skilled and specialist manpower for Oil & Gas, Power, Healthcare and construction megaprojects." },
  { icon: Building2, title: "Real Estate Development", desc: "Design, build and manage mixed-use, medical and industrial real estate across the GCC and India." },
];

const SECTORS = [
  { icon: Fuel, name: "Oil & Gas", image: "https://images.unsplash.com/photo-1776196463688-5f21c9632c75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwb2lsJTIwcmlnJTIwbmlnaHR8ZW58MHx8fHwxNzg2MTkxMDUxfDA&ixlib=rb-4.1.0&q=85" },
  { icon: Zap, name: "Power & Utilities", image: "https://images.unsplash.com/photo-1527335988388-b40ee248d80c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NjE5MTA1MXww&ixlib=rb-4.1.0&q=85" },
  { icon: HeartPulse, name: "Healthcare", image: "https://images.unsplash.com/photo-1516549655169-df83a0774514?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwaG9zcGl0YWwlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NjE5MTA1MXww&ixlib=rb-4.1.0&q=85" },
  { icon: Car, name: "Automotive", image: "https://images.unsplash.com/photo-1647427060118-4911c9821b82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxhdXRvbW90aXZlJTIwbWFudWZhY3R1cmluZyUyMHJvYm90fGVufDB8fHx8MTc4NjE5MTA1MXww&ixlib=rb-4.1.0&q=85" },
];

const VALUES = [
  { icon: Shield, k: "01", t: "Safety First", d: "Zero-incident execution is our license to operate." },
  { icon: Sparkles, k: "02", t: "Quality", d: "Engineered precision on every weld, bolt and byte." },
  { icon: Rocket, k: "03", t: "Timely Delivery", d: "Optimised schedules that respect the critical path." },
  { icon: Handshake, k: "04", t: "Customer First", d: "Long-term partnerships over transactional wins." },
];

const CLIENT_LOGOS = [
  "Bapco", "Tatweer", "Aramco", "GPIC", "Alba", "KOC", "KNPC",
  "MEW Kuwait", "EWA Bahrain", "Sabic", "Petronet", "Occidental",
];

function useProjects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  return projects;
}

export default function Home() {
  const [chatOpen, setChatOpen] = useState(false);
  const projects = useProjects();

  return (
    <div className="relative z-10">
      <Navbar onOpenChat={() => setChatOpen(true)} />

      {/* ============ HERO ============ */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] pt-24 pb-16 overflow-hidden"
      >
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44] via-[#0A1F44]/70 to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="mb-6 inline-flex items-center gap-3 border border-volt/50 bg-volt/5 pl-3 pr-4 py-1.5">
                <span className="inline-flex w-2 h-2 rounded-full bg-volt animate-pulse" />
                <span className="font-mono text-[11px] tracking-[0.3em] text-volt uppercase">Headquartered in Bahrain</span>
              </div>
              <div className="font-mono text-[11px] tracking-[0.3em] text-white/60 uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-px bg-white/40" /> Bahrain · India · Kuwait
              </div>
              <h1
                data-testid="hero-title"
                className="font-display font-bold uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tight"
              >
                Building the industrial<br />
                <span className="text-volt">backbone</span> of the GCC —<br />
                now with AI on board.
              </h1>
              <p className="mt-8 max-w-2xl text-base sm:text-lg text-zinc-300 leading-relaxed">
                A <span className="text-volt font-semibold">Bahrain-based</span> diversified conglomerate — Precision Contracting Network Company W.L.L — serving Oil &amp; Gas, Power &amp; Utilities,
                Healthcare and Automotive across the GCC. EPC, IT, skill training, recruitment and real
                estate solutions from Manama to Hyderabad to Kuwait City.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="#contact"
                  data-testid="hero-cta-contact"
                  className="btn-volt px-6 py-3 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2"
                >
                  Start a project <ArrowUpRight size={14} />
                </a>
                <button
                  onClick={() => setChatOpen(true)}
                  data-testid="hero-cta-chat"
                  className="btn-ghost px-6 py-3 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2"
                >
                  <Cpu size={14} /> Ask PCN.ai anything
                </button>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                {[
                  { k: "20+", v: "Years in industry" },
                  { k: "3", v: "Countries" },
                  { k: "50+", v: "Marquee clients" },
                  { k: "24/7", v: "AI assistance" },
                ].map((s) => (
                  <div key={s.v} className="bg-[#0F2952] p-6">
                    <div className="font-display text-4xl text-volt">{s.k}</div>
                    <div className="font-mono text-[10px] uppercase text-zinc-400 mt-1 tracking-widest">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="hairline" />

      {/* ============ SERVICES ============ */}
      <section id="services" data-testid="services-section" className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader kicker="01 / Core Solutions" title="What we engineer" />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-14">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group bg-[#0F2952] p-8 hover:bg-[#153264] transition-colors duration-200 relative"
              >
                <div className="flex items-start justify-between mb-6">
                  <s.icon size={28} className="text-volt group-hover:scale-110 transition-transform duration-200" />
                  <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest">0{i + 1}</span>
                </div>
                <h3 className="font-display uppercase text-xl mb-3">{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                <div className="mt-6 flex items-center gap-1 text-zinc-500 group-hover:text-volt transition-colors font-mono text-[11px] uppercase tracking-widest">
                  learn more <ArrowUpRight size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="hairline" />

      {/* ============ SECTORS ============ */}
      <section id="sectors" data-testid="sectors-section" className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader kicker="02 / Sectors" title="Industries we power" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {SECTORS.map((s) => (
              <div
                key={s.name}
                data-testid={`sector-${s.name}`}
                className="group relative overflow-hidden border border-white/10 aspect-[3/4]"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                  style={{ backgroundImage: `url(${s.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="relative h-full p-6 flex flex-col justify-end">
                  <s.icon className="text-volt mb-3" size={22} />
                  <div className="font-display uppercase text-xl">{s.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="hairline" />

      {/* ============ PROJECTS ============ */}
      <section id="projects" data-testid="projects-section" className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader kicker="03 / Recent Projects" title="Delivered. On spec. On time." />
          {projects.length === 0 ? (
            <div className="mt-14 font-mono text-sm text-zinc-500">Loading projects…</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
              {projects.map((p) => (
                <article
                  key={p.id}
                  data-testid={`project-card-${p.id}`}
                  className="group border border-white/10 bg-[#0F2952] hover:border-volt transition-colors duration-200 flex flex-col"
                >
                  {p.image_url && (
                    <div
                      className="aspect-video bg-cover bg-center border-b border-white/10 group-hover:brightness-110 transition"
                      style={{ backgroundImage: `url(${p.image_url})` }}
                    />
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
                      <span className="text-volt">{p.sector}</span>
                      <span>{p.year}</span>
                    </div>
                    <h3 className="font-display uppercase text-lg leading-tight mb-3">{p.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed flex-1">{p.scope}</p>
                    <div className="border-t border-white/10 mt-6 pt-4 flex items-center justify-between text-xs font-mono text-zinc-400">
                      <span>{p.client}</span>
                      <span className="flex items-center gap-1"><MapPin size={11} /> {p.location}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="hairline" />

      {/* ============ VALUES ============ */}
      <section data-testid="values-section" className="py-24 bg-[#081936]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader kicker="04 / Vision & Values" title="How we operate" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-14">
            {VALUES.map((v) => (
              <div key={v.k} className="bg-[#0A1F44] p-8">
                <div className="flex items-center justify-between mb-6">
                  <v.icon size={22} className="text-volt" />
                  <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest">{v.k}</span>
                </div>
                <div className="font-display uppercase text-lg mb-2">{v.t}</div>
                <p className="text-sm text-zinc-400 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="hairline" />

      {/* ============ CLIENTS ============ */}
      <section id="clients" data-testid="clients-section" className="py-24 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader kicker="05 / Clientele" title="Trusted by industry leaders" />
        </div>
        <div className="mt-14 relative">
          <div className="flex gap-14 whitespace-nowrap animate-marquee w-max">
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((c, i) => (
              <div
                key={i}
                data-testid={`client-logo-${i}`}
                className="font-display uppercase text-3xl md:text-4xl tracking-wide text-zinc-500 hover:text-volt transition-colors"
              >
                {c}
              </div>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0A1F44] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0A1F44] to-transparent" />
        </div>
      </section>

      <div className="hairline" />

      {/* ============ CONTACT ============ */}
      <ContactSection />

      <Footer />
      <AiChatWidget open={chatOpen} setOpen={setChatOpen} />
    </div>
  );
}

function SectionHeader({ kicker, title }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="font-mono text-[11px] tracking-[0.3em] text-volt uppercase flex items-center gap-3">
        <span className="w-8 h-px bg-volt" /> {kicker}
      </div>
      <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
        {title}
      </h2>
    </div>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", interest: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    try {
      setBusy(true);
      await api.post("/contacts", form);
      toast.success("Message received. The Precision Contracting Network team will get back within 1 business day.");
      setForm({ name: "", email: "", company: "", phone: "", interest: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <SectionHeader kicker="06 / Contact" title="Tell us what you&apos;re building." />
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-lg">
            Whether it's an EPC package, a data-center fit-out or a batch of certified technicians —
            share a few lines and our team will respond within one business day.
          </p>
          <div className="mt-10 space-y-4 font-mono text-sm text-zinc-300">
            <div className="flex items-start gap-3"><MapPin size={16} className="text-volt mt-0.5 shrink-0" /> Office 2239, Building 2504, Road 2832, Block 428 – Al Seef, Manama, Bahrain</div>
            <div className="flex items-center gap-3"><Phone size={16} className="text-volt" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
                <span><span className="text-white/50 mr-2">Office</span> +973 17567929</span>
                <a
                  href="https://wa.me/97333340804?text=Hello%20Precision%20Contracting%20Network%2C%20I%27d%20like%20to%20discuss%20a%20project."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="group inline-flex items-center gap-2 hover:text-volt transition-colors"
                  aria-label="Chat with us on WhatsApp"
                >
                  <span className="text-white/50 mr-1">Mobile</span>
                  <span>+973 33340804</span>
                  <span className="inline-flex items-center gap-1 border border-volt/40 group-hover:border-volt text-volt px-1.5 py-0.5 text-[9px] tracking-widest uppercase">
                    <WhatsAppIcon size={11} /> WhatsApp
                  </span>
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3"><Mail size={16} className="text-volt" /> <a href="mailto:slatha@srihgroup.com" className="hover:text-volt transition-colors">slatha@srihgroup.com</a></div>
            <div className="flex items-center gap-3"><FileText size={16} className="text-volt" /> CR 120601-01</div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="border border-white/10 bg-[#0F2952] p-6 md:p-10 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" value={form.name} onChange={set("name")} testid="contact-name" required />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" required />
            <Field label="Company" value={form.company} onChange={set("company")} testid="contact-company" />
            <Field label="Phone" value={form.phone} onChange={set("phone")} testid="contact-phone" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Interest</label>
            <select
              value={form.interest}
              onChange={set("interest")}
              data-testid="contact-interest"
              className="w-full bg-transparent border border-white/15 focus:border-volt outline-none px-3 py-3 text-sm"
            >
              <option value="" className="bg-[#0F2952]">— select service —</option>
              <option className="bg-[#0F2952]">Construction & Industrial / EPC</option>
              <option className="bg-[#0F2952]">Information Technology</option>
              <option className="bg-[#0F2952]">Skill Training & Development</option>
              <option className="bg-[#0F2952]">Recruitment Services</option>
              <option className="bg-[#0F2952]">Real Estate</option>
              <option className="bg-[#0F2952]">Other</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Message *</label>
            <textarea
              value={form.message}
              onChange={set("message")}
              rows={5}
              required
              data-testid="contact-message"
              className="w-full bg-transparent border border-white/15 focus:border-volt outline-none px-3 py-3 text-sm resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="contact-submit"
            className="btn-volt px-6 py-3 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? "Sending…" : (<>Send request <Send size={12} /></>)}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border border-white/15 focus:border-volt outline-none px-3 py-3 text-sm"
      />
    </div>
  );
}
